// The end of a payment, wherever the bank happens to drop the payer.
//
// The outcome rides in the query string — `?result=&code=&amount=&gateway=` —
// because a redirect from another origin is all we get: no React state, no
// router state, no v1 row to poll (a v2 GatewayPayment has none). The landing
// route moved from `/gateway-callback/` to `/funds/` once it was clear that
// old bundles live behind a service worker for days: `/funds` renders on every
// bundle ever shipped and ignores params it does not know, so the outcome can
// travel without waiting for anyone's service worker. `/gateway-callback` is
// still the legacy v1 target, so both routes read the result through here
// rather than growing two copies of it.

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'

import {
    clearPaymentFlow,
    isSubscriptionFlow,
    readPaymentFlow,
} from './paymentFlow'

/** The four keys the backend sends; stripped from the URL once handled. */
const PARAMS = ['result', 'code', 'amount', 'gateway']

/**
 * Payments already counted, keyed by the payment itself.
 *
 * Module scope on purpose. A ref only guards one mounted component, and
 * `/funds` is the app's landing screen: it re-renders on every react-query
 * focus refetch, remounts on navigation back to it, and runs its effects twice
 * under StrictMode. A payment must be counted once, so the guard has to
 * outlive the component that noticed it.
 */
const counted = new Set<string>()

export interface GatewayReturn {
    /** the bank's verdict, or null on an ordinary visit to the page */
    result: string | null
    isSuccess: boolean
    /** zero-padded server-side — a string, never a number */
    paymentCode: string | null
    amount: number | null
    gateway: string | null
}

interface Options {
    /**
     * Runs once, only for a payment that succeeded, right after the terminal
     * event. For a route that owes the user something beyond being counted —
     * the legacy callback page finishes a v1 cashin registration here.
     */
    onSuccess?: (outcome: GatewayReturn) => void
}

export default function useGatewayReturn({ onSuccess }: Options = {}) {
    const [params] = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()

    const result = params.get('result')
    const paymentCode = params.get('code')
    const gateway = params.get('gateway')
    const rawAmount = params.get('amount')
    const amount = Number(rawAmount) || null

    const outcome: GatewayReturn = {
        result,
        isSuccess: result === 'success',
        paymentCode,
        amount,
        gateway,
    }

    // The callback fires once and the handler may navigate; keeping it in a
    // ref means a re-render with a fresh closure cannot re-arm the effect.
    const handler = useRef(onSuccess)
    handler.current = onSuccess

    useEffect(() => {
        // The overwhelming majority of visits to these routes are ordinary
        // navigation. No verdict in the URL, nothing happened.
        if (!result) return

        const key = `${result}|${paymentCode}|${amount}|${gateway}`
        if (counted.has(key)) return
        counted.add(key)

        const flow = readPaymentFlow()
        const props = {
            result,
            payment_code: paymentCode,
            amount,
            gateway,
            flow_type: flow?.flow_type ?? 'unknown',
            box_id: flow?.box_id ?? null,
        }
        posthog.capture(
            result === 'success' ? 'payment_succeeded' : 'payment_failed',
            props
        )

        if (result === 'success' && isSubscriptionFlow(flow?.flow_type)) {
            // The only event that says a paid subscription was actually
            // bought. The Order settles inside the backend's verify, so
            // nothing on the way out of this app can see it happen.
            posthog.capture('subscription_purchased', {
                flow_type: flow?.flow_type,
                amount: flow?.amount ?? amount,
                month_count: flow?.month_count ?? null,
                box_id: flow?.box_id ?? null,
                box_type: flow?.box_type ?? null,
                gateway: flow?.gateway ?? gateway,
            })
        }

        clearPaymentFlow()

        // Take the verdict out of the URL. `counted` already covers this
        // session, but a reload starts a fresh module — and a payment the user
        // can re-announce by pressing back or refresh is a payment counted
        // twice. Anything else in the query string is left alone.
        const rest = new URLSearchParams(params)
        PARAMS.forEach((key) => rest.delete(key))
        const search = rest.toString()
        navigate(
            { pathname: location.pathname, search: search ? `?${search}` : '' },
            { replace: true }
        )

        if (result === 'success') handler.current?.(outcome)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, paymentCode, amount, gateway])

    return outcome
}
